import Big from 'big.js'
import { Either, left, right } from 'fp-ts/lib/Either'

import { Asset } from 'types/Asset'
import { Loadable } from 'utils/loadable'

export function toSignificantFigures(n: Big, precision: number): string {
  if (n.eq(0)) {
    return n.toString()
  }
  // Replicates the logic of the round_sigfig() database function
  return (
    n
      .toFixed(
        Math.max(0, precision - Math.floor(Math.log10(n.abs().toNumber())) - 1),
      )
      // Strip trailing zeroes after the decimal point, effectively
      // 'trim_scale()'
      .replace(/(\.)(\d*?)0+$/, '$1$2')
      .replace(/\.$/, '')
  )
}

/**
 * Determine if a given asset + selected currency combination requires
 * conversion
 */
export const getNeedsConversion = (
  fromAsset: Asset | undefined,
  selectedCurrency: string,
): boolean => {
  if (fromAsset === undefined) return false
  // Native currency doesn't need conversion by definition
  if (selectedCurrency === 'Native') return false
  // We only need to convert if we're actually going to a different asset
  return selectedCurrency !== fromAsset.ticker
}

/**
 * Handle cases that `Big` would crash on, returning a renderable fallback
 * element
 */
export const getBigOrFallback = (
  n: Big | number | string | undefined | null,
): Either<string, Big> => {
  if (n === undefined || n === null) {
    return left('')
  }
  switch (+n) {
    case Infinity:
      return left('∞')
    case -Infinity:
      return left('-∞')
    default:
      if (Number.isNaN(+n)) {
        return left('NaN')
      }
  }
  return right(Big(n))
}

export const getAssetOrFallback = (
  asset: Loadable<Asset | undefined>,
): Either<string, Asset> => {
  if (asset.loading) {
    return left('...')
  } else if (asset.data === undefined) {
    return left('Unknown asset')
  }
  return right(asset.data)
}
